/**
 * Hook réutilisable pour gérer les abonnements Realtime sur les chantiers et leurs métriques.
 * Migré : ajoute l'invalidation React Query en parallèle du callback onChange.
 *
 * @param entrepriseId - ID de l'entreprise pour filtrer les chantiers
 * @param onChange - Fonction callback appelée lors de changements (backward compat)
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryKeys';
import { queueOrExecuteInvalidation } from '@/lib/modalProtection';

export function useChantiersRealtime(
  entrepriseId: string | null,
  onChange: () => void
) {
  const queryClient = useQueryClient();

  // Debounce pour éviter les rafales d'invalidations lors de la reconnexion WebSocket
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Si une modal de saisie est ouverte, mettre en file d'attente
      queueOrExecuteInvalidation(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.chantiers.all(entrepriseId) });
        queryClient.invalidateQueries({ queryKey: ['charts', entrepriseId] });
        onChange();
      });
    }, 500);
  }, [entrepriseId, onChange, queryClient]);

  useEffect(() => {
    if (!entrepriseId) return;

    // Canal pour les changements sur la table chantiers
    const chantiersChannel = supabase
      .channel(`chantiers:${entrepriseId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chantiers',
          filter: `entreprise_id=eq.${entrepriseId}`,
        },
        handleChange
      )
      .subscribe();

    // Canal pour les affectations (inclut les retards avec notes "Retard chantier")
    const affectationsChannel = supabase
      .channel(`affectations:${entrepriseId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affectations_chantiers',
        },
        (payload) => {
          if (import.meta.env.DEV) console.log('[Realtime] Affectation change detected:', payload.eventType);
          handleChange();
        }
      )
      .subscribe();

    // Cleanup
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(chantiersChannel);
      supabase.removeChannel(affectationsChannel);
    };
  }, [entrepriseId, handleChange, queryClient]);
}
